import React, { useEffect } from 'react';
import PricingSection from '../../components/Home/PricingSection';

const CANONICAL = 'https://explanatum.co.uk/modules/social-media-planner';

const SocialMediaPlannerLanding: React.FC = () => {
  useEffect(() => {
    const title = 'AI Social Media Planner | Explanatum';
    const desc = 'Plan, schedule, and optimize social media campaigns from one AI workspace. Auto-generate posts, queue content, and analyze performance faster than Hootsuite or Fiverr consultants.';

    document.title = title;

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setOg = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', desc);
    setOg('og:title', title);
    setOg('og:description', desc);
    setOg('og:url', CANONICAL);

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', CANONICAL);
  }, []);

  const track = (event: string, params: Record<string, unknown> = {}) => {
    try {
      const w = window as unknown as { gtag?: (...args: unknown[]) => void };
      if (w && typeof w.gtag === 'function') w.gtag('event', event, params);
    } catch {}
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What does the Social Media Planner include?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'Calendar, queue, analytics, campaign manager, media library, and account connectors. Everything is powered by AI prompts and synced to your Explanatum workspace.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can it post to multiple platforms?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'Yes. Connect LinkedIn, Facebook, Instagram, TikTok, and more. The planner schedules, reschedules, and tracks engagement in one place.',
        },
      },
      {
        '@type': 'Question',
        name: 'How fast can we produce campaigns?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'Draft a month of content in minutes. Drag-and-drop schedules, auto-generate captions, and push approved posts live without leaving the planner.',
        },
      },
      {
        '@type': 'Question',
        name: 'Does it work with the AI Business Consultant?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'Absolutely. The consultant can create campaign briefs, draft replies, and coordinate outreach using the same post library.',
        },
      },
      {
        '@type': 'Question',
        name: 'How is this different from Hootsuite or Fiverr?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'You get unlimited AI assistance, integrated analytics, and CRM handoffs without per-seat fees or slow freelancer back-and-forth.',
        },
      },
    ],
  } as const;

  return (
    <main className="bg-white" role="main">
      {/* Hero */}
      <section aria-label="Social media planner overview" className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-sky-400/40 bg-sky-500/10 px-4 py-1 text-sm font-semibold text-sky-700">
                <span className="inline-flex h-2 w-2 rounded-full bg-sky-300" />
                AI Social Media Planner
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
                Schedule, analyze, and scale campaigns without extra headcount.
                <span className="mt-2 block text-blue-600">
                  Calendar, content, and analytics in one AI workspace.
                </span>
              </h1>
              <p className="mt-6 text-lg text-gray-600 max-w-xl">
                Replace manual spreadsheets and scattered tools. Explanatum plans your content calendar, keeps queues filled, and surfaces performance insights while syncing with consultants and CRM records.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href="#pricing"
                  onClick={() => track('click_social_planner_cta', { placement: 'hero' })}
                  className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-sky-500 via-indigo-500 to-blue-500 px-6 py-3 text-base font-semibold text-white shadow-lg shadow-sky-500/30 transition hover:scale-[1.02]"
                >
                  Start 7-day trial
                </a>
                <a
                  href="/marketing/social-media-planner"
                  className="inline-flex items-center justify-center rounded-full border border-blue-600/50 px-6 py-3 text-base font-semibold text-gray-600 hover:bg-blue-50 transition-colors"
                >
                  Open the planner
                </a>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                  Drag-and-drop calendar & queue
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-sky-400" />
                  AI caption drafts for every platform
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-blue-600" />
                  Analytics tied to CRM conversions
                </span>
              </div>
            </div>
            <aside className="relative" aria-label="Planner preview">
              <div className="absolute -top-6 -left-6 h-32 w-32 rounded-full bg-blue-50 blur-3xl" aria-hidden="true" />
              <div className="absolute -bottom-10 -right-8 h-36 w-36 rounded-full bg-cyan-50 blur-3xl" aria-hidden="true" />
              <div className="relative rounded-3xl border border-gray-200 bg-white p-6 shadow-2xl ">
                <header className="flex items-center justify-between text-xs uppercase tracking-widest text-gray-500">
                  <span>Calendar</span>
                  <span>Queue & analytics</span>
                </header>
                <div className="mt-5 space-y-5">
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">Monthly view with drag-and-drop scheduling, AI prompts for gaps, and quick access to campaign notes.</p>
                    <p className="mt-3 text-[12px] text-gray-500">Switch to queue for real-time status across LinkedIn, Instagram, TikTok, and more.</p>
                  </article>
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">Analytics summarize reach, clicks, and attributed pipeline. Export insights to the Business Consultant for follow-ups.</p>
                  </article>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section aria-label="Benefits" className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">AI campaign operations tied to measurable outcomes</h2>
            <p className="mt-4 text-base text-gray-500">Keep publishing consistent, understand what works, and route next steps into sales and success teams.</p>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[{
              title: 'Unified command center',
              body: 'Calendar, queue, analytics, and media library blend into one fast interface so teams stop bouncing between tabs.'
            }, {
              title: 'AI-assisted publishing',
              body: 'Generate platform-specific captions, hashtags, and creative briefs. Approve once and schedule everywhere.'
            }, {
              title: 'Real-time monitoring',
              body: 'Track scheduled, published, and failed posts with automatic LinkedIn job synchronization.'
            }, {
              title: 'Campaign insights',
              body: 'Attribute reach and leads back to campaigns and sync those metrics with your CRM for executive reporting.'
            }, {
              title: 'Consultant collaboration',
              body: 'Ask the AI Business Consultant to repurpose top-performing posts into emails, ads, and sales cadences.'
            }, {
              title: 'Media asset control',
              body: 'Manage visual assets with version history so every post stays on-brand and compliant.'
            }].map((item) => (
              <article key={item.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{item.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section id="compare" aria-label="Comparison" className="relative overflow-hidden bg-white py-20">
        <div className="max-w-6xl mx-auto px-6 relative">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Why teams upgrade from Hootsuite and Fiverr social managers</h2>
            <p className="mt-4 text-base text-gray-500">Automate the busywork while keeping strategy, execution, and insights in sync.</p>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[{
              title: 'Explanatum AI Social Media Planner',
              points: [
                'Unlimited posts, calendars, and analytics included in your subscription',
                'AI caption drafts, drag-and-drop rescheduling, and CRM-integrated analytics',
                'Queue syncing with LinkedIn jobs plus cross-platform scheduling',
                'Media library, campaign manager, and consultant collaboration built-in',
                'One workspace for marketers, founders, and sales follow-up'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }, {
              title: 'Hootsuite & point solutions',
              points: [
                'Seat-based pricing that scales poorly for larger teams',
                'Limited AI assistance—caption generation and analytics require add-ons',
                'Campaign notes, CRM handoffs, and consultant collaboration live elsewhere',
                'Switching tools to manage media assets or run analytics',
                'Automation focuses on publishing, not conversion follow-through'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }, {
              title: 'Fiverr social media consultants',
              points: [
                '£150–£500 per month with variable quality and limited availability',
                'Manual briefs, feedback loops, and revision cycles slow campaigns',
                'No shared analytics or CRM integration—you rebuild reports yourself',
                'Hard to scale across multiple brands or products',
                'Strategy walks out the door when freelancers move on'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }].map((column) => (
              <article key={column.title} className={`rounded-3xl border ${column.accent} p-6 shadow-xl shadow-lg`}>
                <h3 className="text-lg font-semibold text-gray-900">{column.title}</h3>
                <ul className="mt-5 space-y-3 text-sm text-gray-900">
                  {column.points.map((point) => (
                    <li key={point} className="flex items-start gap-3 text-gray-900">
                      <span className="mt-1 inline-flex h-1.5 w-1.5 rounded-full bg-white" />
                      <span className="text-gray-900">{point}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
          <div className="mt-14 rounded-3xl border border-gray-200 bg-sky-400/10 p-8 text-center ">
            <h3 className="text-2xl font-semibold text-gray-900">“Need a full content calendar before Monday?”</h3>
            <p className="mt-3 text-base text-gray-600">Generate the posts, schedule them across platforms, and route best performers to your consultant for repurposing—without waiting on freelancers.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <a
                href="#pricing"
                onClick={() => track('click_social_planner_cta', { placement: 'compare' })}
                className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-sky-500 via-indigo-500 to-blue-600 px-6 py-3 text-base font-semibold text-gray-900 shadow-lg shadow-sky-500/30 hover:scale-[1.02]"
              >
                Run a content sprint
              </a>
              <a
                href="/modules/business-consultant"
                className="inline-flex items-center justify-center rounded-full border border-blue-600/60 px-6 py-3 text-base font-semibold text-gray-600 hover:border-slate-300/70"
              >
                Pair with the Consultant
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Scenarios */}
      <section aria-label="Use cases" className="bg-white py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Where teams rely on the Social Media Planner</h2>
            <p className="mt-4 text-base text-gray-500">Align marketing, sales, and success around the same content pipeline.</p>
          </div>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {[{
              title: 'Product launch orchestration',
              body: 'Coordinate countdown posts, influencer updates, and campaign recaps with AI-generated briefs.'
            }, {
              title: 'Founder-led thought leadership',
              body: 'Draft multi-week posting series, schedule LinkedIn jobs, and measure inbound deals directly.'
            }, {
              title: 'Customer lifecycle campaigns',
              body: 'Plan onboarding, upsell, and advocacy posts while feeding results back into CRM automations.'
            }].map((scenario) => (
              <article key={scenario.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{scenario.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{scenario.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section aria-label="FAQs" className="bg-white py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-semibold text-gray-900">Frequently asked questions</h2>
          <div className="mt-6 space-y-4">
            {[{
              q: 'Does the planner support approval workflows?',
              a: 'Yes—share drafts with stakeholders, collect edits, and log approvals before publishing.'
            }, {
              q: 'Can we import existing social calendars?',
              a: 'Upload CSVs or connect your existing scheduler to migrate posts and analytics in minutes.'
            }, {
              q: 'What about engagement responses?',
              a: 'Use Explanatum to draft replies and escalate leads to the CRM or Business Consultant for follow-up.'
            }, {
              q: 'Is there a media asset limit?',
              a: 'No hard caps. Organize images, videos, and templates with tags and version control for the whole team.'
            }].map((item) => (
              <details key={item.q} className="group rounded-2xl border border-gray-200 bg-white p-5 shadow">
                <summary className="cursor-pointer text-lg font-semibold text-gray-900 group-open:text-sky-700">{item.q}</summary>
                <p className="mt-3 text-sm text-gray-500">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      {/* Pricing */}
      <section id="pricing" aria-label="Pricing" className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <PricingSection />
        </div>
      </section>
    </main>
  );
};

export default SocialMediaPlannerLanding;
